'use client';

import { c as _c } from "react/compiler-runtime";
import { useClientABSmartlyExperiments } from '@repo/platform-absmartly/hooks/useClientABSmartlyExperiments';
import { ABTestVariant } from '@repo/platform-absmartly/utils/variants';
import { useFormAction } from '@repo/platform-analytics/hooks/useFormAction';
import { usePageAction } from '@repo/platform-analytics/hooks/usePageAction';
import { useRouter } from 'next/navigation';
import type { FunnelSession } from '@/entities/session/model/types';
import { CARDFINDER_THINKING_INTERSTITIAL_ID } from '@/shared/config/experiments';
import type { BranchId } from '@/shared/config/funnel/constants';
import { CARDFINDER_FLOW_NAME, FUNNEL_ENTRY_PARAM, FUNNEL_ENTRY_VALUE, RECOMMENDATIONS_PATH } from '@/shared/config/funnel/flowConstants';
import { withCurrentSearchParams } from '@/shared/lib/searchParams';
import { useFunnelStepId } from './Context/hooks';
import { useFunnelProgression } from './in-funnel/progression';

/**
 * The end of a funnel session: commit it, then show the results.
 *
 * This is the funnel's completion signal — reached only from `resumeAfterStep`
 * when the current step ends the branch.
 *
 * `session` must be the funnel session the final step's answer produced. The caller has it;
 * a render captured earlier would not include that answer or its session id.
 */
export function useFinishFunnel() {
  const $ = _c(10);
  const router = useRouter();
  const {
    finish
  } = useFunnelProgression();
  const stepId = useFunnelStepId();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      actionName: "cc_recs_submit",
      actionSection: "cardfinder_flow",
      actionType: "submit"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const firePageAction = usePageAction(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      formName: CARDFINDER_FLOW_NAME,
      sectionName: "cardfinder_funnel"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fireFormAction = useFormAction(t1);
  const {
    results,
    isReady
  } = useClientABSmartlyExperiments(CARDFINDER_THINKING_INTERSTITIAL_ID);
  const variant = results[0];
  let t2;
  if ($[2] !== finish || $[3] !== fireFormAction || $[4] !== firePageAction || $[5] !== isReady || $[6] !== router || $[7] !== stepId || $[8] !== variant) {
    t2 = async (branchId, session) => {
      firePageAction({
        additional_attributes: {
          branch_id: branchId
        }
      });
      fireFormAction({
        formAction: "submitted",
        stepName: stepId,
        additionalAttributes: {
          page_branch_name: branchId
        }
      });
      const update = await finish(session);
      if (update.kind === "start-over") {
        return;
      }
      if (isReady && variant === ABTestVariant.Treatment) {
        router.push(withCurrentSearchParams(`${RECOMMENDATIONS_PATH}?${FUNNEL_ENTRY_PARAM}=${FUNNEL_ENTRY_VALUE}`));
      } else {
        router.push(withCurrentSearchParams(`${RECOMMENDATIONS_PATH}`));
      }
    };
    $[2] = finish;
    $[3] = fireFormAction;
    $[4] = firePageAction;
    $[5] = isReady;
    $[6] = router;
    $[7] = stepId;
    $[8] = variant;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}